import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { BILLING_ALERT_MAX_THRESHOLD, BILLING_ALERT_PRODUCT_NAMES, BillingAlert, BillingAlertProduct, formatCurrencyAmount, getCurrencyDecimals, getCurrencySymbol } from '../../../../../../../../../typings/BillingAlert.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import Input from '../../../../../common/Form/Fields/Input/Input';
import Switch from '../../../../../common/Form/Fields/Switch';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';

// Top-aligned so an error under the title grows the row downward, leaving the input level
// with the switch.
const StyledAlertRow = styled(StyledCardSection)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({
  theme
}) => theme.spacing(4)};

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

// The amount input is 32px tall; the switch and title line match it so they share its
// centre line.
const INPUT_HEIGHT = 32;
const TITLE_LINE_HEIGHT = 20;
const StyledTitleRow = styled(Div)`
  min-height: ${({
  theme
}) => theme.pxToRem(INPUT_HEIGHT)};
`;

// Red 12px message 4px under the title text: pulled up by the space the taller title line
// leaves below it, and inset past the 31px switch and its 12px gap.
const ERROR_GAP = 4;
const StyledError = styled(Text)`
  display: flex;
  align-items: center;
  margin: ${({
  theme
}) => theme.pxToRem(ERROR_GAP - (INPUT_HEIGHT - TITLE_LINE_HEIGHT) / 2)} 0 0;
  padding-left: ${({
  theme
}) => theme.pxToRem(43)};
`;

// The bare Input wrapper only draws a border; match TextInput's shadow outline, focus ring,
// disabled fill and error state.
const StyledAmount = styled(Input)`
  width: 240px;
  flex-shrink: 0;

  input {
    padding-left: ${({
  theme
}) => theme.spacing(7)} !important;
    padding-right: ${({
  theme
}) => theme.spacing(12.5)} !important;
    text-align: right;
    font-variant-numeric: tabular-nums;
    border: 0 !important;
    box-shadow: ${({
  theme
}) => theme.shadows.input.neutral} !important;

    &:focus {
      box-shadow: ${({
  theme
}) => theme.shadows.input.focused} !important;
    }

    &:disabled {
      background-color: ${({
  theme
}) => theme.colors.surface.base.disabled};
      cursor: not-allowed;
    }

    &[aria-invalid='true']:not(:focus) {
      box-shadow: ${({
  theme
}) => theme.shadows.input.error} !important;
      background-color: ${({
  theme
}) => theme.colors.surface.container.danger};
    }
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

// The $ and currency code inside the amount input, centred on its full height.
const AFFIX_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none'
};

// The saved threshold, as the input shows it. An alert that's off has none, even if the
// server still remembers an old amount.
const savedThreshold = (alert?: BillingAlert) => alert?.enabled && alert.threshold !== null && alert.threshold !== undefined ? alert.threshold.toFixed(getCurrencyDecimals(alert.currency)) : '';

// The smallest and largest amounts an alert accepts in a currency.
const thresholdLimits = (currency: string) => {
  const decimals = getCurrencyDecimals(currency);
  // Divide rather than multiply by fractions so 0.01 and 999999999.99 stay exact.
  const unit = 1 / 10 ** decimals;
  const max = Math.floor(BILLING_ALERT_MAX_THRESHOLD * 10 ** decimals) / 10 ** decimals;
  return {
    decimals,
    unit,
    max
  };
};

// What's valid, e.g. "from $0.01 to $999,999,999.99, with up to 2 decimals".
const thresholdRange = (currency: string) => {
  const {
    decimals,
    unit,
    max
  } = thresholdLimits(currency);
  return `from ${formatCurrencyAmount(unit, currency)} to ${formatCurrencyAmount(max, currency)}${decimals > 0 ? `, with up to ${decimals} decimals` : ', without decimals'}`;
};

// The amount as typed, with thousands separators removed, so "999,999,999.99" (how the limits
// are written back to the user) parses like "999999999.99". Only well-formed groups of three
// count: "12,5" is left as is and fails validation instead of silently becoming 125.
const THOUSANDS_GROUPED = /^\d{1,3}(,\d{3})+(\.\d*)?$/;
const normalizeAmount = (value: string) => {
  const trimmed = value.trim();
  return THOUSANDS_GROUPED.test(trimmed) ? trimmed.replace(/,/g, '') : trimmed;
};

// Returns an error message when the (normalized) amount isn't valid in the alert's currency.
const validateThreshold = (value: string, currency: string) => {
  const {
    decimals,
    unit,
    max
  } = thresholdLimits(currency);
  const pattern = decimals > 0 ? new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`) : /^\d+$/;
  if (pattern.test(value) && Number(value) >= unit && Number(value) <= max) return undefined;
  return `Enter an amount ${thresholdRange(currency)}.`;
};
const BillingAlerts: React.FC<{
  product: BillingAlertProduct;
}> = ({
  product
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization_role
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const can_edit = organization_role === 'owner' || organization_role === 'admin';
  const {
    data: alert,
    error: error_0,
    mutate
  } = useSWR(APIMethodKeys.billing.getAlert(product), () => HookdeckAPI.billing.getAlert(product),
  // Client errors (e.g. no subscription) are permanent; only retry server errors.
  {
    shouldRetryOnError: error => !error.status || error.status >= 500
  });
  const [enabled_draft, setEnabledDraft] = useState<boolean>();
  const [threshold_draft, setThresholdDraft] = useState<string>();
  const [save_error, setSaveError] = useState<string>();
  const [is_saving, setSaving] = useState(false);
  const [is_focus_requested, setFocusRequested] = useState(false);
  // Like TextInput's "touched": no error while the amount is first typed, then once it has
  // been left, the error tracks every keystroke instead of waiting for the next blur.
  const [is_touched, setTouched] = useState(false);
  const input_ref = useRef<HTMLInputElement>(null);
  const is_ready = !!alert && !error_0;
  const is_enabled = enabled_draft ?? alert?.enabled ?? false;
  const threshold = threshold_draft ?? savedThreshold(alert);

  // Turning the switch on enables the input; focus it once it's enabled.
  useEffect(() => {
    if (!is_focus_requested || !is_enabled) return;
    input_ref.current?.focus();
    setFocusRequested(false);
  }, [is_focus_requested, is_enabled]);

  // Saves straight away: a threshold to turn the alert on, or null to turn it off.
  const persist = async (value: number | null) => {
    setSaving(true);
    try {
      const updated = await HookdeckAPI.billing.setAlert(product, value);
      await mutate(updated, false);
      setEnabledDraft(undefined);
      // Keep anything typed while the save was in flight.
      setThresholdDraft(draft => {
        if (draft === undefined) return undefined;
        const amount = normalizeAmount(draft);
        const is_saved = value === null ? amount === '' : Number(amount) === value;
        return is_saved ? undefined : draft;
      });
      setSaveError(undefined);
      addToast('success', value === null ? 'Usage alert disabled' : 'Usage alert threshold enabled');
    } catch (error_1: any) {
      setSaveError(error_1?.response?.message || 'Could not save this alert. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Runs when the amount loses focus: save a valid amount, otherwise explain what's valid.
  const commitThreshold = () => {
    if (!can_edit || !is_enabled || is_saving || !alert) return;
    setTouched(true);
    const value_0 = normalizeAmount(threshold);
    const validation_error = validateThreshold(value_0, alert.currency);
    if (validation_error) {
      setSaveError(validation_error);
      return;
    }
    if (alert.enabled && Number(value_0) === alert.threshold) return;
    void persist(Number(value_0));
  };
  const toggle = (is_checked: boolean) => {
    setEnabledDraft(is_checked);
    setSaveError(undefined);
    setTouched(false);
    if (is_checked) {
      setFocusRequested(true);
      return;
    }
    // Off means no threshold, so clear it rather than keep it greyed out.
    setThresholdDraft('');
    // Only an alert that's saved as on needs turning off on the server.
    if (alert?.enabled) void persist(null);
  };
  const product_name = BILLING_ALERT_PRODUCT_NAMES[product];
  const field_id = `${product}_threshold`;
  const error_id = `${field_id}-error`;
  const load_error = error_0?.status === 404 ? `Available when you activate ${product_name}.` : error_0 && error_0.status < 500 && error_0.response?.message;
  return <Div m={{
    b: 10
  }}>
      <Text bold size="l" as="h2" m={0}>
        Usage alert
      </Text>
      <Text muted as="p" m={{
      b: 4
    }}>
        Email organization owners and admins once {product_name} usage charges reach a given
        threshold.
      </Text>
      <form noValidate onSubmit={event => {
      // Enter saves the same way clicking away does.
      event.preventDefault();
      input_ref.current?.blur();
    }}>
        <StyledCard>
          <StyledAlertRow p={{
          y: 3,
          l: 4,
          r: 3
        }}>
            <Div>
              <StyledTitleRow flex={{
              align: 'center',
              gap: 3
            }}>
                {!alert && !error_0 ? <Loading /> : <Tooltip tooltip="Only organization owners and admins can change usage alerts." disabled={can_edit || !is_ready}>
                    <Switch checked={is_enabled} disabled={!can_edit || !is_ready || is_saving} onChange={event_0 => toggle(event_0.target.checked)} />
                  </Tooltip>}
                <Div flex={{
                align: 'center',
                gap: 1
              }}>
                  <Text as="label" htmlFor={is_ready ? field_id : undefined} m={0}>
                    Set usage alert threshold{' '}
                    <Text as="span" muted>
                      (per billing period)
                    </Text>
                  </Text>
                  {/* align="left" lines up bottom edges, so this long tooltip grows upward and
                      fits even though this row sits at the bottom of the page. */}
                  <Tooltip align="left" tooltip="Thresholds apply to usage charges per billing period, before prepaid credits, invoice discounts, and taxes. Base fees are excluded. Usage can take up to an hour to reach billing, so an alert may arrive after the threshold is crossed. Event processing continues after an alert.">
                    <Icon muted icon="info" />
                  </Tooltip>
                </Div>
              </StyledTitleRow>
              {save_error && <StyledError size="xs" as="p" danger role="alert" id={error_id}>
                  <Icon icon="error" left={1} />
                  {save_error}
                </StyledError>}
            </Div>
            {is_ready && <StyledAmount inputContainer={children => <Div style={{
            position: 'relative'
          }}>
                    <Text muted as="span" style={{
              ...AFFIX_STYLE,
              left: 12
            }}>
                      {getCurrencySymbol(alert.currency)}
                    </Text>
                    {children}
                    <Text neutral as="span" style={{
              ...AFFIX_STYLE,
              right: 12
            }}>
                      {alert.currency}
                    </Text>
                  </Div>}>
                <input ref={input_ref} id={field_id} name={field_id} type="text" inputMode="decimal" placeholder="0.00" aria-describedby={save_error ? error_id : undefined} aria-invalid={!!save_error} disabled={!can_edit || !is_enabled} value={is_enabled ? threshold : ''} onChange={event_1 => {
              setThresholdDraft(event_1.target.value);
              setSaveError(is_touched ? validateThreshold(normalizeAmount(event_1.target.value), alert.currency) : undefined);
            }} onBlur={commitThreshold} />
              </StyledAmount>}
          </StyledAlertRow>
          {error_0 && <StyledCardSection p={{
          x: 4,
          y: 3
        }}>
              {load_error ? <Text muted={error_0.status === 404} danger={error_0.status !== 404} size="s">
                  {load_error}
                </Text> : <Text danger size="s">
                  Could not load this alert.{' '}
                  <Button ghost small onClick={() => void mutate()}>
                    Retry
                  </Button>
                </Text>}
            </StyledCardSection>}
        </StyledCard>
      </form>
    </Div>;
};
export default BillingAlerts;